var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { typography, focusVisible } from '../../styles/style-mixins';
import { useClickEvent } from '../../hooks/useClickEvent';
import CloseButton from '../CloseButton';
import { AccessibilityError } from '../../util/errors';
const BORDER_WIDTH = '1px';
const tagBaseStyles = ({ theme }) => css `
  display: inline-flex;
  align-items: center;
  margin: 0;
  word-break: break-word;
  border: ${BORDER_WIDTH} solid var(--cui-border-normal);
  border-radius: ${theme.borderRadius.byte};
  padding: calc(${theme.spacings.bit} - 1px) ${theme.spacings.kilo};
  cursor: default;
  background-color: var(--cui-bg-normal);
  transition: opacity ${theme.transitions.default},
    color ${theme.transitions.default},
    background-color ${theme.transitions.default},
    border-color ${theme.transitions.default};
`;
const tagRemovableStyles = ({ theme, removable }) => removable &&
    css `
    padding-right: calc(${theme.spacings.bit} + ${theme.spacings.tera});
  `;
const tagClickableStyles = ({ onClick }) => onClick &&
    css `
    cursor: pointer;
    outline: 0;
    text-align: left;

    &:hover {
      color: var(--cui-fg-normal-hovered);
      background-color: var(--cui-bg-normal-hovered);
      border-color: var(--cui-border-normal-hovered);
    }

    &:active {
      color: var(--cui-fg-normal-pressed);
      background-color: var(--cui-bg-normal-pressed);
      border-color: var(--cui-border-normal-pressed);
    }

    ${focusVisible()};
  `;
const tagSelectedStyles = ({ selected }) => selected &&
    css `
    background-color: var(--cui-bg-accent-strong);
    border-color: var(--cui-border-accent);
    color: var(--cui-fg-on-strong);
  `;
const tagSelectedClickableStyles = ({ selected, onClick }) => selected &&
    onClick &&
    css `
    &:hover {
      color: var(--cui-fg-on-strong-hovered);
      background-color: var(--cui-bg-accent-strong-hovered);
      border-color: var(--cui-border-accent-hovered);
    }

    &:active {
      color: var(--cui-fg-on-strong-pressed);
      background-color: var(--cui-bg-accent-strong-pressed);
      border-color: var(--cui-border-accent-pressed);
    }
  `;
const TagElement = styled('div')(typography('one'), tagBaseStyles, tagRemovableStyles, tagClickableStyles, tagSelectedStyles, tagSelectedClickableStyles);
const prefixStyles = (theme) => css `
  flex-shrink: 0;
  margin-left: -${theme.spacings.bit};
  margin-right: ${theme.spacings.bit};
`;
const suffixStyles = (theme) => css `
  flex-shrink: 0;
  margin-left: ${theme.spacings.bit};
  margin-right: -${theme.spacings.bit};
`;
const closeButtonStyles = ({ theme }) => css `
  position: absolute;
  top: 50%;
  right: ${BORDER_WIDTH};
  transform: translateY(-50%);
  border-radius: ${theme.borderRadius.byte};
  border: 0;
`;
const RemoveButton = styled(CloseButton)(closeButtonStyles);
const Container = styled.div `
  position: relative;
`;
export const Tag = forwardRef((_a, ref) => {
    var { children, prefix: Prefix, suffix: Suffix, onRemove, removeButtonLabel, selected, onClick, tracking, className, style } = _a, props = __rest(_a, ["children", "prefix", "suffix", "onRemove", "removeButtonLabel", "selected", "onClick", "tracking", "className", "style"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        onRemove &&
        !removeButtonLabel) {
        throw new AccessibilityError('Tag', 'The `removeButtonLabel` prop is missing. Omit the `onRemove` prop if you intend to disable the tag removing functionality.');
    }
    const as = onClick ? 'button' : 'div';
    const handleClick = useClickEvent(onClick, tracking, 'tag');
    return (_jsxs(Container, Object.assign({ className: className, style: style }, { children: [_jsxs(TagElement, Object.assign({ removable: Boolean(onRemove), selected: selected, onClick: handleClick, type: onClick && 'button', as: as, ref: ref }, props, { children: [Prefix && _jsx(Prefix, { css: prefixStyles }), children, Suffix && _jsx(Suffix, { css: suffixStyles })] })), onRemove && removeButtonLabel && (_jsx(RemoveButton, { type: "button", variant: selected ? 'primary' : 'secondary', label: removeButtonLabel, "data-testid": "tag-close", size: "kilo", onClick: onRemove, tracking: tracking ? Object.assign({ component: 'tag-remove' }, tracking) : undefined }))] })));
});
Tag.displayName = 'Tag';
